import { CSSProperties, useEffect, useState } from 'react';
import {
  DefaultFooter,
  DropdownMenu,
  Selected,
  MultiControlProps,
  useMultiAutocomplete,
  MultiAutocompleteControl,
  DefaultItem,
} from '@adsk/alloy-react-dropdown';
import styled, { CSSProp } from 'styled-components';
import noop from 'lodash/noop';
import Portal from '@adsk/alloy-react-portal';
import { EMPTY_ARRAY } from '../../constants';
import { translate } from '../../helpers';

const MultiSelectDropdownWrapper = styled.div`
  width: 100%;
`;

export type MultiSelectDropdownProps<T extends DefaultItem> = {
  id?: string;
  options: Array<T>;
  selectedItems?: Array<T>;
  style?: CSSProperties;
  placeholder?: string;
  searchPlaceholder?: string;
  onChange: (selectedItems: Array<T>) => void;
  ['data-testid']?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  error?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  itemToString?: (item: T | null) => string;
};

const selectedItemToString = <T extends DefaultItem>(item: T | null) =>
  (item?.label as unknown as string) ?? '';

type MultiControlRenderItemsArgs<T extends DefaultItem> = Parameters<
  NonNullable<MultiControlProps<T>['renderItems']>
>[0];

const renderItems = <T extends DefaultItem>(props: MultiControlRenderItemsArgs<T>) => (
  <Selected {...props} itemToString={selectedItemToString} />
);

export const MultiSelectDropdown = <T extends DefaultItem>({
  id,
  options,
  selectedItems = EMPTY_ARRAY,
  onChange,
  placeholder,
  searchPlaceholder = translate(`defaultSearchPlaceholder`),
  style = {},
  'data-testid': dataTestId = 'MultiSelectDropdown',
  disabled,
  clearable,
  className,
  error,
  isOpen: isOpenProp = false,
  onClose = noop,
  itemToString,
}: MultiSelectDropdownProps<T>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpenProp);
  useEffect(() => {
    setIsMenuOpen(isMenuOpen);
  }, [isMenuOpen]);

  const { controlProps, menuProps } = useMultiAutocomplete<T>({
    id,
    items: options,
    selectedItems,
    onSelectedItemsChange: (changes) => {
      onChange(changes.selectedItems || []);
    },
    isOpen: isMenuOpen,
    onIsOpenChange: ({ isOpen: _isOpen = false }) => {
      if (!_isOpen) {
        onClose?.();
      }

      setIsMenuOpen(_isOpen);
    },
    ...(itemToString ? { itemToString } : {}),
  });
  const showClearableFooter = clearable && selectedItems.length > 0;
  return (
    <MultiSelectDropdownWrapper css={style as CSSProp} className={className}>
      <MultiAutocompleteControl<T>
        {...controlProps}
        disabled={disabled}
        placeholder={isMenuOpen ? searchPlaceholder : placeholder}
        data-testid={dataTestId}
        error={error}
        renderItems={renderItems}
      />
      <Portal container={document.body}>
        <DropdownMenu
          renderFooter={() =>
            showClearableFooter ? <DefaultFooter onClear={() => onChange([])} /> : null
          }
          {...menuProps}
        />
      </Portal>
    </MultiSelectDropdownWrapper>
  );
};
